// profile.actions.ts

import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { UpdateProfileRequest, User } from '../../../../../models';
import { LoginMethodsEnum } from 'src/app/components/auth/login-alt/user-login-form-alt/user-login-form-alt.component';
import { LoginMethodStatusEnum } from 'src/app/components/profile/security-settings/login-methods/login-methods.component';

/**
 * Login Method Result Type
 */
export interface LoginMethodResult {
  method: LoginMethodsEnum;
  enabled: LoginMethodStatusEnum;
  reason: string;
}

/**
 * Profile Update Actions
 * Handles user profile updates (name, bio, etc.)
 */
export const userProfileActions = createActionGroup({
  source: 'Profile/Update',
  events: {
    'Update': props<{ payload: UpdateProfileRequest }>(),
    'Update Success': props<{ learmoProfile: User }>(),
    'Update Failure': props<{ error: any }>(),
  },
});

/**
 * Email Update Actions
 * Handles email change requests with password verification
 */
export const userEmailActions = createActionGroup({
  source: 'Profile/Email',
  events: {
    'Update': props<{ payload: { email: string; password: string } }>(),
    'Update Success': props<{ response: Partial<User> }>(),
    'Update Failure': props<{ error: any }>(),
  },
});

/**
 * Two-Factor Authentication Actions
 * Handles enabling/disabling 2FA
 */
export const enable2FAActions = createActionGroup({
  source: 'Profile/2FA',
  events: {
    'Enable': emptyProps(),
    'Enable Success': emptyProps(),
    'Enable Failure': props<{ error: any }>(),
    'Disable': emptyProps(),
    'Disable Success': emptyProps(),
    'Disable Failure': props<{ error: any }>(),
  },
});

/**
 * Login Method Configuration Actions
 * Handles fetching and updating available login methods
 */
export const userLoginMethodConfigActions = createActionGroup({
  source: 'Profile/Login Methods',
  events: {
    'Get': emptyProps(),
    'Get Success': props<{ loginMethodsResults: LoginMethodResult[] }>(),
    'Get Failure': props<{ error: any }>(),
  },
});

/**
 * Change Login Method Actions
 * Handles enabling/disabling specific login methods (email, phone, magic link, etc.)
 */
export const changeLoginMethodConfigActions = createActionGroup({
  source: 'Profile/Change Login Method',
  events: {
    'Change': props<{ method: LoginMethodsEnum; enabled: boolean }>(),
    'Change Success': props<{
      method: LoginMethodsEnum;
      enabled: LoginMethodStatusEnum;
    }>(),
    'Change Failure': props<{ error: any }>(),
  },
});

/**
 * Password Update Actions
 * Handles password changes for authenticated users
 */
export const passwordUpdateActions = createActionGroup({
  source: 'Profile/Password',
  events: {
    'Update': props<{ currentPassword: string; newPassword: string }>(),
    'Update Success': emptyProps(),
    'Update Failure': props<{ error: any }>(),
  },
});

/**
 * Avatar Update Actions
 * Handles profile picture uploads and updates
 */
export const avatarUpdateActions = createActionGroup({
  source: 'Profile/Avatar',
  events: {
    'Upload': props<{ file: File }>(),
    'Upload Success': props<{ avatar: User['avatar'] }>(),
    'Upload Failure': props<{ error: any }>(),
    'Remove': emptyProps(),
    'Remove Success': emptyProps(),
    'Remove Failure': props<{ error: any }>(),
  },
});